import { useState, useEffect } from 'react';
import axios from 'axios';

export type PlatformFlags = Record<string, boolean>;

/** Grieg's unauthenticated, no-cache release-flags endpoint (COM-111). */
export const PLATFORM_FLAGS_ENDPOINT = '/api/v1/static/flags';

const DEFAULT_POLL_INTERVAL_MS = 60_000;

/**
 * Polls Grieg's `/api/v1/static/flags` endpoint for the resolved release-flags
 * map. Grieg serves this uncached, sourced from henry-rollins, so a flag flip
 * reaches the browser within roughly the poll interval (~1 minute by default).
 *
 * Never throws — on any error the last-known flags are retained (empty on a
 * cold failure), mirroring henry-rollins' default-false resilience.
 *
 * @param pollIntervalMs - How often to re-fetch the flags, in milliseconds.
 * @returns The current resolved flags map (empty until the first fetch lands).
 */
export function usePlatformFlags(pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS): PlatformFlags {
  const [flags, setFlags] = useState<PlatformFlags>({});

  useEffect(() => {
    let active = true;

    const fetchFlags = async () => {
      try {
        const { data } = await axios.get<{ flags: PlatformFlags }>(PLATFORM_FLAGS_ENDPOINT);
        if (active && data?.flags) setFlags(data.flags);
      } catch {
        // Keep the last-known flags on error — never surface a failure to the UI.
      }
    };

    fetchFlags();
    const timer = window.setInterval(fetchFlags, pollIntervalMs);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [pollIntervalMs]);

  return flags;
}
